import { Controller } from "@hotwired/stimulus"

// Android's WebView has no built-in PDF renderer, so we can't navigate the
// WebView to a raw PDF stream — it shows "Error loading page". Instead this
// controller fetches the inline PDF stream and draws each page to a canvas.
//
// pdf.js (~1.8 MB) is dynamically imported so it only loads on the viewer page,
// not in the app-wide bundle.
export default class extends Controller {
  static values = { url: String }
  static targets = ["pages", "status", "passwordForm", "passwordInput", "passwordError"]

  connect() {
    this.renderDocument()
  }

  async renderDocument() {
    try {
      const pdfjsLib = await import("pdfjs-dist")
      pdfjsLib.GlobalWorkerOptions.workerSrc = (
        await import("pdfjs-dist/build/pdf.worker.min.mjs?url")
      ).default

      // pdf.js v6 requires an object ({ url }); it no longer accepts a bare string.
      const loadingTask = pdfjsLib.getDocument({ url: this.urlValue })

      // Encrypted PDFs: pdf.js pauses loading and calls onPassword; the promise
      // stays pending until updatePassword() is called with the correct password
      // (the callback re-fires with INCORRECT_PASSWORD after a wrong one).
      loadingTask.onPassword = (updatePassword, reason) => {
        this.updatePassword = updatePassword
        this.askForPassword(reason === pdfjsLib.PasswordResponses.INCORRECT_PASSWORD)
      }

      const pdf = await loadingTask.promise
      this.passwordFormTarget.classList.add("hidden")
      if (this.hasStatusTarget) this.statusTarget.remove()

      // Render at the container width, sharpened for the screen's pixel ratio
      // (capped so long documents don't blow up canvas memory).
      const cssWidth = this.pagesTarget.clientWidth
      const ratio = Math.min(window.devicePixelRatio || 1, 2)

      for (let n = 1; n <= pdf.numPages; n++) {
        const page = await pdf.getPage(n)
        const base = page.getViewport({ scale: 1 })
        const viewport = page.getViewport({ scale: (cssWidth / base.width) * ratio })

        const wrapper = document.createElement("div")
        wrapper.className = "relative"
        this.pagesTarget.appendChild(wrapper)

        const canvas = document.createElement("canvas")
        canvas.width = viewport.width
        canvas.height = viewport.height
        canvas.className = "block w-full h-auto bg-white shadow rounded"
        wrapper.appendChild(canvas)

        await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise
        await this.overlayLinks(pdfjsLib, page, viewport, wrapper)
      }
    } catch (error) {
      this.passwordFormTarget.classList.add("hidden")
      if (this.hasStatusTarget) {
        this.statusTarget.classList.remove("hidden")
        this.statusTarget.textContent = "Couldn't display this PDF."
      }
      console.error("pdf-viewer:", error)
    }
  }

  askForPassword(incorrect) {
    if (this.hasStatusTarget) this.statusTarget.classList.add("hidden")
    this.passwordFormTarget.classList.remove("hidden")
    this.passwordErrorTarget.classList.toggle("hidden", !incorrect)
    this.passwordInputTarget.select()
    this.passwordInputTarget.focus()
  }

  submitPassword(event) {
    event.preventDefault()
    this.passwordErrorTarget.classList.add("hidden")
    this.updatePassword(this.passwordInputTarget.value)
  }

  // The canvas is pure pixels, so hyperlinks in the PDF are rasterized away.
  // Overlay each external link annotation with a transparent <a>, positioned
  // by percentage — the canvas displays at CSS width, not its internal
  // (devicePixelRatio-scaled) size, so percentages keep hotspots aligned.
  async overlayLinks(pdfjsLib, page, viewport, wrapper) {
    const annotations = await page.getAnnotations({ intent: "display" })

    for (const annotation of annotations) {
      if (annotation.subtype !== "Link" || !annotation.url) continue

      const [x1, y1, x2, y2] = pdfjsLib.Util.normalizeRect(
        viewport.convertToViewportRectangle(annotation.rect)
      )

      const link = document.createElement("a")
      link.href = annotation.url
      link.target = "_blank"
      link.rel = "noopener noreferrer"
      link.className = "absolute"
      link.style.left = `${(x1 / viewport.width) * 100}%`
      link.style.top = `${(y1 / viewport.height) * 100}%`
      link.style.width = `${((x2 - x1) / viewport.width) * 100}%`
      link.style.height = `${((y2 - y1) / viewport.height) * 100}%`
      wrapper.appendChild(link)
    }
  }
}
